<script setup>
const props = defineProps({
    radioButtonConfig: {
        type: Object,
        required: false,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                label: rawValue => rawValue?.label === undefined || typeof rawValue?.label === 'string',
                uid: rawValue => rawValue?.uid === undefined || typeof rawValue?.uid === 'string',
                name: rawValue => rawValue?.name === undefined || typeof rawValue?.name === 'string',
                value: rawValue => rawValue?.value === undefined || typeof rawValue?.value === 'string',
                required: rawValue => rawValue?.required === undefined || typeof rawValue?.required === 'string',
                val: rawValue => rawValue?.val === undefined || typeof rawValue?.val === 'string',
                conditionalText: rawValue => rawValue?.conditionalText === undefined || typeof rawValue?.conditionalText === 'string',
                validationRules: rawValue => rawValue?.validationRules === undefined || typeof rawValue?.validationRules === 'object',
            }

            return Object.values(validations).every(validation => validation(rawValue))
        }
    }
})

//  Creates a form field with its validation state, you will use this inside your custom input components.
const { value, errorMessage, meta, resetField } = useField(() =>
    props.radioButtonConfig?.name,
props.radioButtonConfig?.validationRules
)

// Show error + success classes in case fields have validation rules
const _requiresValidation = ref(props.radioButtonConfig?.validationRules)

const requiresValidation = computed(() => _requiresValidation.value !== undefined)

const formFieldClass = computed(() => {
    return {
        'form-error': !meta.valid && requiresValidation.value,
        'form-success': meta.valid && meta.dirty && requiresValidation.value,
        'form-input-radio-group-axis-vertical': props?.radioButtonConfig?.axis === 'vertical',
        'form-input-radio-group-alignment-vertical': props?.radioButtonConfig?.alignment === 'center'
    }
})

// Change initial value with dynamic data of input field
resetField({
    value: props.radioButtonConfig?.val
})

// Use watcher to update data with dynamic values (seems to be necessary...?)
watch(() => props.radioButtonConfig?.val, () => {
    resetField({
        value: props.radioButtonConfig?.val
    })
})

const selectRadio = () => {
    value.value = props.radioButtonConfig?.value
}
</script>

<template>
    <div class="form-input-radio-wrapper" :class="formFieldClass">
        <input
            :id="radioButtonConfig.uid"
            v-model="value"
            class="form-input-radio"
            type="radio"
            :value="radioButtonConfig.value"
            :required="radioButtonConfig.required"
            :name="radioButtonConfig.name"
            tabindex="-1"
        >

        <label
            class="form-input-radio-label-wrapper"
            :for="radioButtonConfig?.uid"
            tabindex="0"
            @keydown.space.prevent="selectRadio"
            @keydown.enter.prevent="selectRadio"
        >
            <span class="form-input-radio-label">
                <span>{{ radioButtonConfig.label }}</span>
                <em v-if="radioButtonConfig.conditionalText && value === radioButtonConfig.value">
                    {{ radioButtonConfig.conditionalText }}
                </em>
            </span>
        </label>
    </div>
    <FormFieldMessage
        :msg-config="{
            message: errorMessage,
            error: errorMessage && ! meta.valid
        }"
    />
</template>

<style lang="scss">
.form-input-radio-wrapper {
    position: relative;
    margin: 0 0 20px 20px;

    @include bp(medium1) {
        margin: 0 0 20px 40px;
    }

    &.form-input-radio-group-axis-vertical {
        width: 100%;
    }
}

.form-input-radio {
    opacity: 0;
    position: absolute;
    z-index: -1;

    &:checked {
        + label {
            &::after {
                opacity: 1;
            }
        }
    }
}

.form-input-radio-icon {
    margin-inline-end: px2rem(10);
    padding-inline-start: 6px;

    &.icon-credit-card {
        height: 32px;
    }

    &.icon-apple-pay {
        height: 48px;
    }

    &.icon-google-pay {
        height: 48px;
    }
}

.form-input-radio-label-wrapper {
    min-height: 28px;
    margin: 0;
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    width: auto;

    &:focus {
        border: $white 1px dotted;
    }

    &::before {
        content: '';
        width: 28px;
        height: 28px;
        border: $form-field-radio-label-border 2px solid;
        background: $form-field-radio-label-bg;
        border-radius: 100%;
        position: absolute;
        display: block;
        top: 0;
        left: 0;

        .form-input-radio-group-alignment-vertical & {
            top: 50%;
            transform: translateY(-50%);
        }
    }

    &::after {
        content: '';
        width: 14px;
        height: 14px;
        border-radius: 100%;
        background: $form-field-radio-label-checked-bg;
        display: block;
        position: absolute;
        top: 7px;
        left: 7px;
        opacity: 0;
        transition: opacity 400ms ease;

        .form-input-radio-group-alignment-vertical & {
            top: 50%;
            transform: translateY(-50%);
        }
    }
}

.form-input-radio-label {
    padding-inline-start: 34px;
    display: flex;
    flex-flow: row wrap;
    align-items: center;

    span {
        margin-inline-end: px2rem(5);
    }

    em {
        font-style: normal;
        display: inline-block;
    }
}
</style>
